<template>
	<div>
		<v-snackbar
			v-model="snackbar"
			:timeout="10000"
			:top="true"
			color="accent"
			class="black--text"
		>
			{{ snackbarText }}
			<v-btn class="blexon-button" small icon @click="snackbar = false">
				<v-icon>close</v-icon>
			</v-btn>
		</v-snackbar>
		<v-progress-linear v-show="dataLoading" color="accent" indeterminate></v-progress-linear>
		<v-container class="content" grid-list-md fluid>
			<v-layout row>
				<v-flex shrink>
					<h1>Fertigungen</h1>
				</v-flex>
				<v-flex>
					<v-btn @click="goToCapacity()">Auslastung</v-btn>
					<v-btn @click="goToManufacturingOverview()">Produktionsübersicht</v-btn>
				</v-flex>
			</v-layout>
			<div v-for="region in fertigers" v-bind:key="region.regionId" class="region-block">
				<hr class="region-separator" />
				<div class="region-header">
					<h2>{{ region.region }}</h2>
					<v-btn
						v-if="isAdmin"
						icon
						small
						color="accent"
						class="ml-2"
						title="Neuen Fertiger hinzufügen"
						@click="openCreateDialog(parseInt(region.regionId))"
					>
						<v-icon color="black">add</v-icon>
					</v-btn>
				</div>
				<div
					style="
						display: grid;
						grid-gap: 10px;
						grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
					"
				>
					<div v-for="fertiger in region.fertigers" :key="fertiger.id">
						<v-card
							hover
							@click.native="goToFertiger(fertiger)"
							:class="{ 'fertiger-card-hidden': !fertiger.shownInKundenportal }"
						>
							<v-card-title class="pb-0 fertiger-card-title">
								<v-icon
									:color="getAdapterStateColor(fertiger.lastPoll)"
									style="padding-right: 0.2em"
									@click.stop="checkAdapter(fertiger)"
									:title="getLastPollFormatted(fertiger.lastPoll)"
								>
									offline_bolt
								</v-icon>
								<h2
									class="font-weight-light fertiger-card-name"
									:title="fertiger.name"
								>
									{{ fertiger.name }}
								</h2>
								<v-spacer></v-spacer>
								<v-menu offset-y v-model="fertiger.isOpen" close-on-click>
									<v-icon
										slot="activator"
										large
										color="primary"
										@click.stop="fertiger.isOpen = !fertiger.isOpen"
									>
										keyboard_arrow_down
									</v-icon>
									<v-list>
										<v-list-tile
											@click="executeRequest(fertiger)"
											title="Signalisiert dem Adapter jetzt sofort nach neuen Aufträgen zu fragen."
										>
											<v-list-tile-title>
												Abfrage jetzt ausführen
											</v-list-tile-title>
										</v-list-tile>
										<v-list-tile
											@click="updateAdapter(fertiger)"
											title="Signalisiert dem Adapter jetzt nach einer neuen Version zu suchen, diese herunterzuladen und zu installieren."
										>
											<v-list-tile-title> Update Adapter </v-list-tile-title>
										</v-list-tile>
										<v-list-tile
											@click="openConfig(fertiger)"
											title="Lädt die Konfigurationsdatei vom Server. Diese kann anschliessend bearbeitet und gespeichert werden."
										>
											<v-list-tile-title>
												Adapterkonfiguration
											</v-list-tile-title>
										</v-list-tile>
										<v-list-tile
											v-if="isAdmin"
											@click="togglePortalVisibility(fertiger)"
											:title="
												fertiger.shownInKundenportal
													? 'Der Fertiger wird nach dem Umschalten keine Aufträge mehr erhalten und ist im Kundenportal nicht mehr sichtbar.'
													: 'Der Fertiger bekommt im Moment keine Aufträge. Nach dem Umschalten erhält er wieder Aufträge und wird im Kundenportal angezeigt.'
											"
										>
											<v-list-tile-title>
												{{
													fertiger.shownInKundenportal
														? "Keine Aufträge mehr zuweisen"
														: "Aufträge zuweisen"
												}}
											</v-list-tile-title>
										</v-list-tile>
										<v-list-tile
											v-if="isAdmin"
											@click="getAdapterSecret(fertiger)"
											title="Erzeugt ein neues Adapter-Secret und zeigt es einmalig an. Das bisherige Secret wird dabei ungültig."
										>
											<v-list-tile-title>
												Get AdapterSecret
											</v-list-tile-title>
										</v-list-tile>
									</v-list>
								</v-menu>
							</v-card-title>
							<v-card-text style="min-height: 100px" class="pt-0">
								<img
									style="height: 60px; padding-top: 20px"
									onerror="this.style.display = 'none'"
									:src="getFertigerLogo(fertiger.id)"
								/>
							</v-card-text>
							<v-card-actions>
								<span style="font-size: 0.8em" class="ml-2"
									>Adapter Version: {{ fertiger.clientVersion }}</span
								>
								<v-spacer></v-spacer>
							</v-card-actions>
						</v-card>
					</div>
				</div>
			</div>
			<p v-if="fertigers.length <= 0" class="headline">
				<v-icon x-large color="accent">error_outline</v-icon>
				Zugriff nicht erlaubt.
			</p>
		</v-container>
		<v-dialog v-model="showConfig" width="700">
			<v-card>
				<v-card-text>
					<textarea v-model="config" style="width: 100%; height: 80vh"></textarea>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn @click="showConfig = false">Schliessen</v-btn>
					<v-btn class="blexon-button" @click="saveConfig()"> Speichern </v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="confirmAdapterSecret" width="500" persistent>
			<v-card>
				<v-card-title>
					<h3>Neues Adapter-Secret erzeugen?</h3>
				</v-card-title>
				<v-card-text>
					<v-alert type="warning" outlined dense>
						Das bisherige Secret von
						<strong>{{
							pendingAdapterSecretFertiger && pendingAdapterSecretFertiger.name
						}}</strong>
						wird sofort ungültig. Der laufende Adapter kann sich damit nicht mehr
						anmelden, bis das neue Secret bei ihm hinterlegt ist.
					</v-alert>
					<p class="mt-3 mb-0">
						Ein bereits installierter Adapter beim Fertiger kann nach dem Erzeugen des
						neuen Secrets nicht mehr auf das Fertigerportal zugreifen, bis das neue
						Secret dort hinterlegt und der Adapter neu gestartet wurde.
					</p>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="cancelAdapterSecret()">Abbrechen</v-btn>
					<v-btn class="blexon-button" @click="confirmGenerateAdapterSecret()"
						>Neues Secret erzeugen</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="showAdapterSecret" width="600" persistent>
			<v-card>
				<v-card-title>
					<h3>Adapter-Secret</h3>
				</v-card-title>
				<v-card-text>
					<v-alert type="warning" outlined dense>
						Dieses Secret wird nur jetzt angezeigt. Sobald das Fenster geschlossen wird,
						ist es unwiederbringlich verloren - unbedingt jetzt kopieren und beim
						Adapter hinterlegen.
					</v-alert>
					<v-text-field
						label="ClientId"
						:value="adapterSecret.clientId"
						readonly
					></v-text-field>
					<v-text-field
						label="ClientSecret"
						:value="adapterSecret.clientSecret"
						readonly
					></v-text-field>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn class="blexon-button" @click="closeAdapterSecret()">Schliessen</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
		<v-dialog v-model="createDialog" width="600" persistent>
			<v-card>
				<v-card-title>
					<h3>Neuen Fertiger hinzufügen ({{ regionName(newFertiger.regionId) }})</h3>
				</v-card-title>
				<v-card-text>
					<v-form ref="createForm" v-model="createFormValid">
						<v-text-field
							v-model="newFertiger.name"
							label="Name"
							:rules="[requiredRule]"
							required
						></v-text-field>
						<v-text-field
							v-model="newFertiger.street"
							label="Strasse"
							:rules="[requiredRule]"
							required
						></v-text-field>
						<v-layout row>
							<v-flex xs4 class="pr-2">
								<v-text-field
									v-model="newFertiger.zip"
									label="PLZ"
									:rules="[requiredRule]"
									required
								></v-text-field>
							</v-flex>
							<v-flex xs8>
								<v-text-field
									v-model="newFertiger.town"
									label="Ort"
									:rules="[requiredRule]"
									required
								></v-text-field>
							</v-flex>
						</v-layout>
						<v-select
							v-if="countryOptions.length > 1"
							v-model="newFertiger.country"
							:items="countryOptions"
							label="Land"
							:rules="[requiredRule]"
							:loading="loadingCountries"
							required
						></v-select>
						<v-text-field
							v-else
							v-model="newFertiger.country"
							label="Land"
							:loading="loadingCountries"
							readonly
						></v-text-field>
						<v-text-field
							v-model="newFertiger.email"
							label="E-Mail"
							:rules="[emailRule]"
						></v-text-field>
						<v-text-field
							v-model="newFertiger.phone"
							label="Telefonnummer"
						></v-text-field>
					</v-form>
				</v-card-text>
				<v-card-actions>
					<v-spacer></v-spacer>
					<v-btn text @click="createDialog = false" :disabled="creating">Abbrechen</v-btn>
					<v-btn
						class="blexon-button"
						@click="createFertiger()"
						:disabled="creating || !createFormValid"
						:loading="creating"
						>Erstellen</v-btn
					>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
import httpClient from "../store/httpClient";
import moment from "moment";

export default {
	data() {
		return {
			snackbar: false,
			snackbarText: "",
			dataLoading: false,
			isLoading: false,
			showConfig: false,
			config: "",
			fertigerWithOpenConfig: null,
			isOpen: false,
			isAdmin: false,
			regions: [],
			createDialog: false,
			createFormValid: true,
			creating: false,
			showAdapterSecret: false,
			adapterSecret: { clientId: "", clientSecret: "" },
			confirmAdapterSecret: false,
			pendingAdapterSecretFertiger: null,
			countryOptions: [],
			loadingCountries: false,
			newFertiger: {
				regionId: 0,
				name: "",
				street: "",
				zip: "",
				town: "",
				country: "",
				email: "",
				phone: "",
			},
			requiredRule: (v) => !!v || "Pflichtfeld",
			emailRule: (v) => !v || /.+@.+\..+/.test(v) || "Ungültige E-Mail",
		};
	},
	async created() {
		await Promise.all([this.loadAdmin(), this.loadRegions()]);
	},
	computed: {
		fertigers() {
			var groupBy = function (xs, key) {
				return xs.reduce(function (rv, x) {
					(rv[x[key]] = rv[x[key]] || []).push(x);
					return rv;
				}, {});
			};
			const regions = groupBy(this.$store.state.fertiger, "regionId");
			var regionsWithFertigers = [];
			for (var prop in regions) {
				regionsWithFertigers.push({
					regionId: prop,
					region: this.regionName(prop),
					fertigers: regions[prop],
				});
			}
			return regionsWithFertigers;
		},
	},
	methods: {
		getLastPollFormatted(lastPoll) {
			return moment(lastPoll).format("[Letztes Signal am:] D. MMMM YYYY - HH:mm [Uhr]");
		},
		getAdapterStateColor(lastPoll) {
			if (moment().subtract(30, "minutes").isBefore(lastPoll)) {
				return "success";
			} else {
				return "error";
			}
		},

		goToFertiger(fertiger) {
			this.$router.push({ name: "order", params: { fertigerId: fertiger.id } });
		},

		goToCapacity() {
			this.$router.push({ name: "capacity" });
		},

		goToManufacturingOverview() {
			this.$router.push({ name: "manufacturingOverview" });
		},

		getFertigerLogo(id) {
			return `${process.env.VUE_APP_SERVER_URL}api/fertiger/getlogo?fertigerId=${id}`;
		},

		async loadAdmin() {
			try {
				const result = await httpClient().get("api/Capacity/IsUserAdmin");
				this.isAdmin = result.data;
			} catch (err) {
				this.isAdmin = false;
			}
		},

		regionName(id) {
			const numericId = parseInt(id);
			const region = this.regions.find((r) => r.id === numericId);
			return region ? region.name : "Unbekannt";
		},

		async loadRegions() {
			try {
				const response = await httpClient().get("api/Fertiger/GetRegions");
				this.regions = response.data || [];
			} catch (err) {
				this.regions = [];
			}
		},

		async openCreateDialog(regionId) {
			this.newFertiger = {
				regionId,
				name: "",
				street: "",
				zip: "",
				town: "",
				country: "",
				email: "",
				phone: "",
			};
			this.countryOptions = [];
			this.createDialog = true;
			this.$nextTick(() => {
				if (this.$refs.createForm) {
					this.$refs.createForm.resetValidation();
				}
			});
			await this.loadCountriesForRegion(regionId);
		},

		async loadCountriesForRegion(regionId) {
			try {
				this.loadingCountries = true;
				const response = await httpClient().get(
					`api/Fertiger/GetCountriesOfRegion?regionId=${regionId}`
				);
				this.countryOptions = (response.data || []).map((c) => c.name);
				if (this.countryOptions.length === 1) {
					this.newFertiger.country = this.countryOptions[0];
				}
			} catch (err) {
				this.setSnackbarText("Fehler beim Laden der Länder: " + err);
				this.countryOptions = [];
			} finally {
				this.loadingCountries = false;
			}
		},

		async createFertiger() {
			if (this.$refs.createForm && !this.$refs.createForm.validate()) {
				return;
			}
			try {
				this.creating = true;
				await httpClient().post("api/Fertiger/CreateFertiger", this.newFertiger);
				this.setSnackbarText("Fertiger erfolgreich erstellt.");
				this.createDialog = false;
				await this.reloadFertigers();
			} catch (err) {
				this.setSnackbarText("Fehler beim Erstellen: " + err);
			} finally {
				this.creating = false;
			}
		},

		getAdapterSecret(fertiger) {
			fertiger.isOpen = false;
			// Rotating the secret invalidates the one the running adapter currently uses,
			// so require an explicit confirmation before firing the request.
			this.pendingAdapterSecretFertiger = fertiger;
			this.confirmAdapterSecret = true;
		},

		cancelAdapterSecret() {
			this.confirmAdapterSecret = false;
			this.pendingAdapterSecretFertiger = null;
		},

		async confirmGenerateAdapterSecret() {
			const fertiger = this.pendingAdapterSecretFertiger;
			this.confirmAdapterSecret = false;
			this.pendingAdapterSecretFertiger = null;
			if (!fertiger) {
				return;
			}
			try {
				this.dataLoading = true;
				const response = await httpClient().post(
					`api/Fertiger/GetAdapterSecret?fertigerId=${fertiger.id}`,
					null
				);
				this.adapterSecret = {
					clientId: response.data.clientId,
					clientSecret: response.data.clientSecret,
				};
				this.showAdapterSecret = true;
			} catch (err) {
				this.setSnackbarText("Fehler beim Erzeugen des Adapter-Secrets: " + err);
			} finally {
				this.dataLoading = false;
			}
		},

		closeAdapterSecret() {
			this.showAdapterSecret = false;
			// Nach dem Schliessen ist das Secret weg - lokalen State ebenfalls loeschen,
			// damit es sich nicht ueber ein spaeteres Oeffnen wieder anzeigen laesst.
			this.adapterSecret = { clientId: "", clientSecret: "" };
		},

		async togglePortalVisibility(fertiger) {
			fertiger.isOpen = false;
			try {
				this.dataLoading = true;
				const response = await httpClient().post(
					`api/Fertiger/TogglePortalVisibility?fertigerId=${fertiger.id}`,
					null
				);
				this.$store.commit("setShownInKundenportal", {
					fertigerId: fertiger.id,
					value: response.data,
				});
				this.setSnackbarText(
					response.data
						? "Der Fertiger erhält jetzt wieder Aufträge."
						: "Der Fertiger erhält jetzt keine Aufträge mehr."
				);
			} catch (err) {
				this.setSnackbarText("Fehler beim Umschalten: " + err);
			} finally {
				this.dataLoading = false;
			}
		},

		async reloadFertigers() {
			try {
				const response = await httpClient().get("api/Fertiger/GetFertigers");
				this.$store.commit("setFertigers", response.data);
			} catch (err) {
				this.setSnackbarText("Fehler beim Nachladen der Fertiger: " + err);
			}
		},

		async updateAdapter(fertiger) {
			try {
				this.dataLoading = true;
				await httpClient().post(`api/Event/UpdateAdapter?fertigerId=${fertiger.id}`, null);
				this.setSnackbarText("Update erfolgreich eingeleitet.");
			} catch (err) {
				this.setSnackbarText(`Fehler beim Update.` + err);
			} finally {
				this.dataLoading = false;
			}
		},

		async openConfig(fertiger) {
			try {
				this.dataLoading = true;
				this.fertigerWithOpenConfig = fertiger;
				await httpClient().post(
					`api/Event/SaveConfigToServer?fertigerId=${fertiger.id}`,
					null
				);
				this.setSnackbarText("Config wird geladen...");
				await new Promise((r) => setTimeout(r, 2000));
				var configFile = await httpClient().get(
					`api/Fertiger/GetConfig?fertigerId=${fertiger.id}`
				);
				this.config = JSON.stringify(configFile.data, null, 2);
				this.showConfig = true;
			} catch (err) {
				this.setSnackbarText(`Fehler beim Laden des Configfiles.` + err);
			} finally {
				this.dataLoading = false;
			}
		},

		async saveConfig() {
			try {
				this.dataLoading = true;
				await httpClient().post(
					`api/Event/SendConfigToAdapter?fertigerId=${this.fertigerWithOpenConfig.id}`,
					{ Content: this.config }
				);
				this.setSnackbarText("Erfolgreich gespeichert.");
			} catch (err) {
				this.setSnackbarText(`Fehler beim Speichern der Konfigurationsdatei.` + err);
			} finally {
				this.dataLoading = false;
			}
		},

		async executeRequest(fertiger) {
			try {
				this.dataLoading = true;
				await httpClient().post(`api/Event/executeRequest?fertigerId=${fertiger.id}`, null);
				this.setSnackbarText("Signal erfolgreich.");
			} catch (err) {
				this.setSnackbarText(`Fehler bei der Signalisierung.` + err);
			} finally {
				this.dataLoading = false;
			}
		},

		async checkAdapter(fertiger) {
			try {
				this.dataLoading = true;
				await httpClient().post(`api/Event/CheckAdapter?fertigerId=${fertiger.id}`, null);
				this.setSnackbarText("Check erfolgreich.");
			} catch (err) {
				this.setSnackbarText(`Fehler beim Check.` + err);
			} finally {
				this.dataLoading = false;
			}
		},

		/**
		 * Sets the text in the snackbar and shows it.
		 */
		setSnackbarText(text) {
			this.snackbarText = text;
			this.snackbar = true;
		},
	},
};
</script>
<style scoped>
.fertiger-card-hidden {
	border: 2px solid #ef8b20;
}
.fertiger-card-title {
	flex-wrap: nowrap;
	min-width: 0;
}
.fertiger-card-name {
	color: #efbb20;
	margin: 0;
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}
.region-block {
	margin-left: 0;
	margin-right: 0;
}
.region-separator {
	margin: 2em 0 0 0;
	border: none;
	border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.region-header {
	display: flex;
	align-items: center;
	margin: 1.25em 0 1em 0;
}
.region-header h2 {
	margin: 0;
}
</style>
